import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import AlcazarKnowledge from '../components/AlcazarKnowledge';
import AlcazarReporting from '../components/AlcazarReporting';
import AlcazarReenviadas from '../components/AlcazarReenviadas';
import AlcazarAgente from '../components/AlcazarAgente';
import AlcazarTestChat from '../components/AlcazarTestChat';

const EVENT_COLORS = {
  draft_created: { bg: 'bg-green-100', text: 'text-green-800', label: 'Borrador OK' },
  draft_error: { bg: 'bg-red-100', text: 'text-red-800', label: 'Error Draft' },
  learned: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Aprendido' },
  handoff: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Handoff' },
  ignored: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Ignorado' },
  whatsapp_msg: { bg: 'bg-green-100', text: 'text-green-800', label: 'WhatsApp' },
  chat_msg: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Chat' },
  auto_replied: { bg: 'bg-green-100', text: 'text-green-800', label: 'Auto' },
  email_sent: { bg: 'bg-emerald-100', text: 'text-emerald-800', label: 'Enviado' },
  entradas_replied: { bg: 'bg-cyan-100', text: 'text-cyan-800', label: 'Entradas resp.' },
  translated: { bg: 'bg-indigo-100', text: 'text-indigo-800', label: 'Traducido' },
  learning_reply_sent: { bg: 'bg-teal-100', text: 'text-teal-800', label: 'Resp. enviada' },
  patronato: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Patronato' },
};

const GLOBAL_STATUS_COLORS = {
  SENT: { bg: '#dcfce7', text: '#166534', label: 'Enviado' },
  READY_TO_SEND: { bg: '#d1fae5', text: '#065f46', label: 'Listo' },
  WAITING_ENTRADAS: { bg: '#fef3c7', text: '#92400e', label: 'Esperando' },
  MANUAL_REQUIRED: { bg: '#fed7aa', text: '#9a3412', label: 'Manual' },
  PROCESSING: { bg: '#dbeafe', text: '#1e40af', label: 'Procesando' },
  FAILED: { bg: '#fee2e2', text: '#991b1b', label: 'Error' },
};

const ACTION_COLORS = {
  SEND_DRAFT: { bg: '#dcfce7', text: '#166534', label: 'SEND_DRAFT' },
  HANDOFF_ENTRADAS: { bg: '#fef3c7', text: '#92400e', label: 'HANDOFF' },
  SEND_PATRONATO: { bg: '#f3e8ff', text: '#6b21a8', label: 'PATRONATO' },
  handoff: { bg: '#fef3c7', text: '#92400e', label: 'HANDOFF' },
  draft_created: { bg: '#dcfce7', text: '#166534', label: 'DRAFT' },
  email_sent: { bg: '#dcfce7', text: '#166534', label: 'ENVIADO' },
};

function GlobalStatusBadge({ status }) {
  const c = GLOBAL_STATUS_COLORS[status] || { bg: '#f3f4f6', text: '#374151', label: status || '?' };
  return (
    <span style={{
      backgroundColor: c.bg, color: c.text,
      padding: '2px 8px', borderRadius: '9999px',
      fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap',
      display: 'inline-block',
    }}>{c.label}</span>
  );
}

function ActionBadge({ action }) {
  const c = ACTION_COLORS[action] || { bg: '#f3f4f6', text: '#374151', label: action || '-' };
  return (
    <span style={{
      backgroundColor: c.bg, color: c.text,
      padding: '2px 8px', borderRadius: '9999px',
      fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap',
      display: 'inline-block',
    }}>{c.label}</span>
  );
}

function IntentBadge({ intent, confidence, kbTopic, kbId }) {
  if (!intent && !kbTopic) return <span style={{ color: '#9ca3af', fontSize: '12px' }}>-</span>;
  return (
    <div style={{ fontSize: '12px', lineHeight: '1.3' }}>
      {intent && (
        <span style={{
          backgroundColor: '#eff6ff', color: '#1e40af',
          padding: '1px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: 500,
        }}>
          {intent}{confidence ? ` ${(confidence * 1).toFixed(2)}` : ''}
        </span>
      )}
      {(kbTopic || kbId) && (
        <div style={{ marginTop: '2px' }}>
          {kbId && <span style={{ backgroundColor: '#dbeafe', color: '#1e40af', padding: '0 4px', borderRadius: '3px', fontSize: '10px', fontWeight: 600, marginRight: '4px' }}>#{kbId}</span>}
          {kbTopic && kbTopic !== intent && <span style={{ color: '#6b7280', fontSize: '10px' }}>{kbTopic}</span>}
        </div>
      )}
    </div>
  );
}

function XiaDetailPanel({ log }) {
  const e = log.extraction || {};
  return (
    <div style={{
      marginTop: '12px', padding: '12px', backgroundColor: '#f0f9ff',
      borderRadius: '8px', border: '1px solid #bae6fd', fontFamily: 'monospace', fontSize: '12px'
    }}>
      <div style={{ fontWeight: 700, marginBottom: '8px', color: '#0369a1', fontSize: '13px', fontFamily: 'inherit' }}>
        Analisis XIA
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 16px' }}>
        <div><span style={{ color: '#6b7280' }}>Intent:</span> <strong>{log.detected_intent || '-'}</strong>{log.intent_confidence ? ` (conf: ${(log.intent_confidence * 1).toFixed(2)})` : ''}</div>
        <div><span style={{ color: '#6b7280' }}>KB Topic:</span> {log.kb_topic || '-'}</div>
        <div><span style={{ color: '#6b7280' }}>Idioma:</span> {log.xia_language || '-'}{log.language_confidence ? ` (conf: ${(log.language_confidence * 1).toFixed(2)})` : ''}</div>
        <div><span style={{ color: '#6b7280' }}>Real-time:</span> {log.real_time_availability === true ? 'SI' : log.real_time_availability === false ? 'NO' : '-'}</div>
        <div><span style={{ color: '#6b7280' }}>Accion:</span> <ActionBadge action={log.xia_action} /></div>
        <div><span style={{ color: '#6b7280' }}>Estado:</span> <GlobalStatusBadge status={log.global_status} /></div>
        {log.gate_reasons && (
          <div style={{ gridColumn: '1 / -1' }}><span style={{ color: '#6b7280' }}>Gate reasons:</span> {Array.isArray(log.gate_reasons) ? log.gate_reasons.join(', ') : String(log.gate_reasons)}</div>
        )}
        {log.sla_display && (
          <div><span style={{ color: '#6b7280' }}>SLA:</span> {log.sla_display}</div>
        )}
      </div>
      {e && Object.keys(e).length > 0 && (
        <details style={{ marginTop: '8px' }}>
          <summary style={{ cursor: 'pointer', color: '#0369a1', fontSize: '11px', fontFamily: 'inherit' }}>Ver extraction completo</summary>
          <pre style={{ marginTop: '4px', padding: '8px', backgroundColor: '#fff', borderRadius: '4px', border: '1px solid #e5e7eb', overflow: 'auto', maxHeight: '200px', fontSize: '11px' }}>
            {JSON.stringify(e, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
}

const CHANNEL_COLORS = {
  email: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Email' },
  whatsapp: { bg: 'bg-green-100', text: 'text-green-800', label: 'WhatsApp' },
  llamadas: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Llamadas' },
  mia: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'MIA' },
  chat: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Chat' },
  dashboard_chat: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Chat' },
  dashboard_chat_test: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Chat Test' },
};

function ChannelBadge({ channel }) {
  const c = CHANNEL_COLORS[channel] || { bg: 'bg-gray-100', text: 'text-gray-800', label: channel || '?' };
  return <span className={`${c.bg} ${c.text} px-2 py-1 rounded-full text-xs font-medium`}>{c.label}</span>;
}

function EventBadge({ type }) {
  const c = EVENT_COLORS[type] || { bg: 'bg-gray-100', text: 'text-gray-800', label: type };
  return <span className={`${c.bg} ${c.text} px-2 py-1 rounded-full text-xs font-medium`}>{c.label}</span>;
}

function LifecycleIcons({ lifecycle, eventType }) {
  if (!lifecycle && !eventType) return null;
  // For non-handoff events (SEND_DRAFT/email_sent), handoff steps are "skipped"
  const isNonHandoff = !lifecycle && (eventType === 'draft_created' || eventType === 'email_sent');
  const steps = [
    { key: 'forwarded', icon: '\u2197', label: 'Reenviado a entradas', color: '#22c55e' },
    { key: 'entradas_replied', icon: '\u2709', label: 'Entradas respondio', color: '#06b6d4' },
    { key: 'learned', icon: '\u2713', label: 'XIA memorizo', color: '#3b82f6' },
    { key: 'translated', icon: '\u2295', label: 'Traducido 12 idiomas', color: '#8b5cf6' },
    { key: 'sent_to_customer', icon: '\u2192', label: 'Enviado al cliente', color: '#14b8a6' },
  ];
  const SKIPPED_COLOR = '#3b82f6'; // blue for skipped/not-applicable
  const lc = lifecycle || {};
  return (
    <div style={{ display: 'flex', gap: '3px', alignItems: 'center' }}>
      {steps.map(({ key, icon, label, color }) => {
        let bg, fg, tooltip;
        if (isNonHandoff) {
          // Non-handoff: step 1-2 (forwarded, entradas_replied) are skipped, 3-5 depend on event
          if (key === 'forwarded' || key === 'entradas_replied') {
            bg = SKIPPED_COLOR; fg = '#fff'; tooltip = label + ' (n/a - respuesta directa)';
          } else if (key === 'sent_to_customer') {
            bg = eventType === 'email_sent' ? color : '#e5e7eb';
            fg = eventType === 'email_sent' ? '#fff' : '#9ca3af';
            tooltip = label;
          } else {
            bg = '#e5e7eb'; fg = '#9ca3af'; tooltip = label;
          }
        } else {
          const active = lc[key];
          bg = active ? color : '#e5e7eb';
          fg = active ? '#fff' : '#9ca3af';
          tooltip = label;
          if (active && key === 'entradas_replied' && lc.answered_at) {
            tooltip += ' - ' + new Date(lc.answered_at).toLocaleString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'Europe/Madrid' });
          }
        }
        return (
          <span
            key={key}
            title={tooltip}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              fontSize: '11px',
              fontWeight: 'bold',
              backgroundColor: bg,
              color: fg,
              cursor: 'default',
              transition: 'all 0.2s',
            }}
          >
            {icon}
          </span>
        );
      })}
    </div>
  );
}

function StatCard({ label, value, color = 'gray', icon }) {
  const colors = {
    gray: 'bg-gray-50 border-gray-200',
    green: 'bg-green-50 border-green-200',
    yellow: 'bg-yellow-50 border-yellow-200',
    red: 'bg-red-50 border-red-200',
    blue: 'bg-blue-50 border-blue-200',
  };
  return (
    <div className={`${colors[color]} border rounded-lg p-4 text-center`}>
      <div className="text-2xl mb-1">{icon}</div>
      <div className="text-2xl font-bold text-gray-800">{value}</div>
      <div className="text-xs text-gray-500 uppercase">{label}</div>
    </div>
  );
}


// ═══════════════════════════════════════════
// COSTES TAB COMPONENT
// ═══════════════════════════════════════════
function CostesTab() {
  const today = new Date().toISOString().slice(0, 10);
  const firstOfMonth = today.slice(0, 8) + '01';
  const [costesData, setCostesData] = React.useState(null);
  const [dateFrom, setDateFrom] = React.useState(firstOfMonth);
  const [dateTo, setDateTo] = React.useState(today);
  const [filtro, setFiltro] = React.useState('todo');
  const [loadingCostes, setLoadingCostes] = React.useState(false);
  const [numCuentas, setNumCuentas] = React.useState(1);

  React.useEffect(() => {
    setLoadingCostes(true);
    api.get('/api/alcazar/costes', { params: { from: dateFrom, to: dateTo } })
      .then(res => {
        setCostesData(res.data);
        if (res.data.num_cuentas) setNumCuentas(res.data.num_cuentas);
        setLoadingCostes(false);
      })
      .catch(() => setLoadingCostes(false));
  }, [dateFrom, dateTo]);

  const exportCostesCSV = () => {
    const t = localStorage.getItem('token');
    window.open('/api/alcazar/costes/export?from=' + dateFrom + '&to=' + dateTo + '&filtro=' + filtro + '&token=' + (t || ''), '_blank');
  };

  const tabs = [
    { key: 'todo', label: 'TODO' },
    { key: 'mantenimiento', label: 'MANTENIMIENTO' },
    { key: 'variables', label: 'VARIABLES' },
    { key: 'desarrollo', label: 'DESARROLLO' },
  ];

  const showMantenimiento = filtro === 'todo' || filtro === 'mantenimiento';
  const showVariables = filtro === 'todo' || filtro === 'variables';
  const showDesarrollo = filtro === 'todo' || filtro === 'desarrollo';

  // Calculate days in range for prorating
  const daysInRange = Math.max(1, Math.ceil((new Date(dateTo) - new Date(dateFrom)) / (1000*60*60*24)) + 1);
  const daysInMonth = new Date(new Date(dateFrom).getFullYear(), new Date(dateFrom).getMonth() + 1, 0).getDate();
  const monthFraction = Math.min(1, daysInRange / daysInMonth);

  // Prorate fixed costs
  const mantTotal = costesData ? (costesData.total_mantenimiento || 0) * monthFraction / numCuentas : 0;
  const desTotal = costesData ? (costesData.total_desarrollo || 0) * monthFraction / numCuentas : 0;
  const varTotal = costesData ? (costesData.total_variable || 0) : 0;

  const visibleTotal = (showMantenimiento ? mantTotal : 0) + (showVariables ? varTotal : 0) + (showDesarrollo ? desTotal : 0);

  return (
    <div className="space-y-6">
      {/* Barra superior: fechas + filtros + exportar */}
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-2">
          <button onClick={() => { const now = new Date(); const first = now.toISOString().slice(0,8) + '01'; const last = new Date(now.getFullYear(), now.getMonth()+1, 0).toISOString().slice(0,10); setDateFrom(first); setDateTo(last); }}
              className="bg-blue-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold hover:bg-blue-700">
              COSTE MENSUAL
            </button>
            <label className="text-sm font-medium text-gray-600">Desde:</label>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            className="border rounded-lg px-3 py-1.5 text-sm" />
          <label className="text-sm font-medium text-gray-600 ml-2">Hasta:</label>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            className="border rounded-lg px-3 py-1.5 text-sm" />
        </div>
        <div className="flex items-center gap-1">
          {tabs.map(t => (
            <button key={t.key} onClick={() => setFiltro(t.key)}
              className={'px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ' +
                (filtro === t.key ? 'bg-blue-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700')}>
              {t.label}
            </button>
          ))}
        </div>
        <button onClick={exportCostesCSV}
          className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium">
          Exportar CSV
        </button>
      </div>

      {loadingCostes ? (
        <div className="text-center py-8 text-gray-500">Cargando costes...</div>
      ) : costesData ? (
        <>
          {/* Resumen cards - solo las visibles */}
          <div className={'grid gap-4 ' + (filtro === 'todo' ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-1 md:grid-cols-2')}>
            {showMantenimiento && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-blue-700">{mantTotal.toFixed(2)} EUR</div>
                <div className="text-xs text-blue-600 mt-1">Mantenimiento{numCuentas > 1 ? ' (1/' + numCuentas + ' cuentas)' : ''}</div>
              </div>
            )}
            {showDesarrollo && (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-purple-700">{desTotal.toFixed(2)} EUR</div>
                <div className="text-xs text-purple-600 mt-1">Desarrollo{numCuentas > 1 ? ' (1/' + numCuentas + ' cuentas)' : ''}</div>
              </div>
            )}
            {showVariables && (
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-orange-700">{varTotal.toFixed(2)} EUR</div>
                <div className="text-xs text-orange-600 mt-1">Variable (uso)</div>
              </div>
            )}
            {filtro === 'todo' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-green-700">{visibleTotal.toFixed(2)} EUR</div>
                <div className="text-xs text-green-600 mt-1">Total periodo</div>
              </div>
            )}
          </div>

          {/* Info prorrateo */}
          {(daysInRange < daysInMonth || numCuentas > 1) && showMantenimiento && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-2 text-xs text-yellow-800">
              {daysInRange < daysInMonth && <span>Costes fijos prorrateados: {daysInRange} dias de {daysInMonth} ({(monthFraction*100).toFixed(0)}%). </span>}
              {numCuentas > 1 && <span>Costes fijos (mantenimiento + desarrollo) repartidos entre {numCuentas} cuentas.</span>}
            </div>
          )}

          {/* Costes Mantenimiento */}
          {showMantenimiento && (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-4 py-3 bg-blue-50 border-b">
                <h3 className="text-sm font-semibold text-blue-800">Costes de Mantenimiento (infraestructura)</h3>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs text-gray-500">Concepto</th>
                    <th className="px-4 py-2 text-left text-xs text-gray-500">Detalle</th>
                    <th className="px-4 py-2 text-right text-xs text-gray-500">EUR/mes</th>
                    <th className="px-4 py-2 text-right text-xs text-gray-500">EUR periodo{numCuentas > 1 ? ' (1/' + numCuentas + ')' : ''}</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {costesData.fixed_costs?.mantenimiento?.map((c, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-800">{c.concepto}</td>
                      <td className="px-4 py-3 text-gray-600 text-xs">{c.detalle}</td>
                      <td className="px-4 py-3 text-right text-gray-500">{c.eur > 0 ? c.eur.toFixed(2) : 'Incluido'}</td>
                      <td className="px-4 py-3 text-right font-semibold text-gray-800">
                        {c.eur > 0 ? (c.eur * monthFraction / numCuentas).toFixed(2) + ' EUR' : <span className="text-green-600">Incluido</span>}
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-blue-50 font-semibold">
                    <td className="px-4 py-3 text-blue-800" colSpan="3">Total Mantenimiento (periodo)</td>
                    <td className="px-4 py-3 text-right text-blue-800">{mantTotal.toFixed(2)} EUR</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* Costes Desarrollo */}
          {showDesarrollo && (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-4 py-3 bg-purple-50 border-b flex justify-between items-center">
                <h3 className="text-sm font-semibold text-purple-800">Costes de Desarrollo (herramientas){numCuentas > 1 ? ' - 1/' + numCuentas + ' cuentas' : ''}</h3>
                <span className="text-xs text-purple-600 italic">Solo aplica en periodos de desarrollo activo</span>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs text-gray-500">Concepto</th>
                    <th className="px-4 py-2 text-left text-xs text-gray-500">Detalle</th>
                    <th className="px-4 py-2 text-right text-xs text-gray-500">EUR/mes</th>
                    <th className="px-4 py-2 text-right text-xs text-gray-500">EUR periodo</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {costesData.fixed_costs?.desarrollo?.map((c, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-800">{c.concepto}</td>
                      <td className="px-4 py-3 text-gray-600 text-xs">{c.detalle}</td>
                      <td className="px-4 py-3 text-right text-gray-500">{c.eur.toFixed(2)}</td>
                      <td className="px-4 py-3 text-right font-semibold text-gray-800">{(c.eur * monthFraction / numCuentas).toFixed(2)} EUR</td>
                    </tr>
                  ))}
                  <tr className="bg-purple-50 font-semibold">
                    <td className="px-4 py-3 text-purple-800" colSpan="3">Total Desarrollo (periodo)</td>
                    <td className="px-4 py-3 text-right text-purple-800">{desTotal.toFixed(2)} EUR</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* Costes Variables (uso real) */}
          {showVariables && (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-4 py-3 bg-orange-50 border-b">
                <h3 className="text-sm font-semibold text-orange-800">Costes Variables por Uso ({dateFrom} a {dateTo})</h3>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs text-gray-500">Canal</th>
                    <th className="px-4 py-2 text-right text-xs text-gray-500">Total msgs</th>
                    <th className="px-4 py-2 text-right text-xs text-gray-500">Auto</th>
                    <th className="px-4 py-2 text-right text-xs text-gray-500">Handoff</th>
                    <th className="px-4 py-2 text-right text-xs text-gray-500">Traducidos</th>
                    <th className="px-4 py-2 text-right text-xs text-gray-500">Tiempo medio</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-800">
                      <span className="inline-block w-2 h-2 rounded-full bg-blue-500 mr-2"></span>Email
                    </td>
                    <td className="px-4 py-3 text-right">{costesData.usage?.email?.total || 0}</td>
                    <td className="px-4 py-3 text-right text-green-600">{costesData.usage?.email?.auto_sent || 0}</td>
                    <td className="px-4 py-3 text-right text-yellow-600">{costesData.usage?.email?.handoff || 0}</td>
                    <td className="px-4 py-3 text-right text-blue-600">{costesData.usage?.email?.translated || 0}</td>
                    <td className="px-4 py-3 text-right text-gray-600">{costesData.usage?.email?.avg_time_s || 0}s</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-800">
                      <span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-2"></span>WhatsApp
                    </td>
                    <td className="px-4 py-3 text-right">{costesData.usage?.whatsapp?.total || 0}</td>
                    <td className="px-4 py-3 text-right text-green-600">{costesData.usage?.whatsapp?.auto_sent || 0}</td>
                    <td className="px-4 py-3 text-right text-yellow-600">{costesData.usage?.whatsapp?.handoff || 0}</td>
                    <td className="px-4 py-3 text-right text-blue-600">{costesData.usage?.whatsapp?.translated || 0}</td>
                    <td className="px-4 py-3 text-right text-gray-600">{costesData.usage?.whatsapp?.avg_time_s || 0}s</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-800">
                      <span className="inline-block w-2 h-2 rounded-full bg-orange-500 mr-2"></span>Chat
                    </td>
                    <td className="px-4 py-3 text-right">{costesData.usage?.chat?.total || 0}</td>
                    <td className="px-4 py-3 text-right text-green-600">{costesData.usage?.chat?.auto_sent || 0}</td>
                    <td className="px-4 py-3 text-right text-yellow-600">{costesData.usage?.chat?.handoff || 0}</td>
                    <td className="px-4 py-3 text-right text-blue-600">{costesData.usage?.chat?.translated || 0}</td>
                    <td className="px-4 py-3 text-right text-gray-600">{costesData.usage?.chat?.avg_time_s || 0}s</td>
                  </tr>
                  <tr className="bg-gray-50 font-semibold">
                    <td className="px-4 py-3 text-gray-800">TOTAL</td>
                    <td className="px-4 py-3 text-right">{costesData.usage?.totals?.messages || 0}</td>
                    <td className="px-4 py-3 text-right" colSpan="2"></td>
                    <td className="px-4 py-3 text-right text-blue-700">{costesData.usage?.totals?.translated || 0}</td>
                    <td className="px-4 py-3 text-right"></td>
                  </tr>
                </tbody>
              </table>
              <div className="px-4 py-3 bg-orange-50 border-t">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-orange-800">Coste OpenAI traducciones ({costesData.usage?.totals?.translated || 0} x 0.001 EUR)</span>
                  <span className="font-semibold text-orange-800">{varTotal.toFixed(2)} EUR</span>
                </div>
              </div>
            </div>
          )}

          {/* Nota */}
          <div className="bg-gray-50 rounded-lg p-4 text-xs text-gray-500">
            <strong>Notas:</strong> Los agentes del pipeline son codigo local sin coste. Solo el Traductor tiene coste por uso (0.001 EUR/mensaje) y solo cuando el idioma no es espanol.
            {numCuentas > 1 && ' Los costes de mantenimiento se reparten entre ' + numCuentas + ' cuentas activas en el servidor.'}
            {' Los costes de desarrollo (Claude, ChatGPT) son fijos y se reparten entre todas las cuentas/proyectos activos.'}
          </div>
        </>
      ) : (
        <div className="text-center py-8 text-gray-500">No se pudieron cargar los costes</div>
      )}
    </div>
  );
}

// Decode HTML entities for display
function decodeHtml(text) {
  if (!text) return text;
  return text
    .replace(/&nbsp;/gi, ' ')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&amp;/gi, '&')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&#x27;/gi, "'")
    .replace(/&#(\d+);/g, (_, n) => String.fromCharCode(parseInt(n)));
}

function ReportingV4() {
  const today = new Date().toISOString().slice(0, 10);
  const firstOfMonth = today.slice(0, 8) + '01';
  const [dateFrom, setDateFrom] = React.useState(firstOfMonth);
  const [dateTo, setDateTo] = React.useState(today);
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    setLoading(true);
    const from = new Date(dateFrom);
    const to = new Date(dateTo);
    const days = Math.max(1, Math.ceil((to - from) / (1000*60*60*24)) + 1);
    api.get('/api/alcazar/v4-stats', { params: { days } })
      .then(res => { setData(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [dateFrom, dateTo]);

  const waStats = data?.stats?.find(s => s.channel === 'whatsapp') || {};
  const emailStats = data?.stats?.find(s => s.channel === 'email') || {};
  const totalConv = parseInt(waStats.total || 0) + parseInt(emailStats.total || 0);
  const totalResp = parseInt(waStats.respondidas || 0) + parseInt(emailStats.respondidas || 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <label className="text-sm font-medium text-gray-600">Desde:</label>
        <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="border rounded-lg px-3 py-2 text-sm" />
        <label className="text-sm font-medium text-gray-600">Hasta:</label>
        <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="border rounded-lg px-3 py-2 text-sm" />
      </div>

      {loading ? (
        <div className="text-center py-8 text-gray-500">Cargando...</div>
      ) : data ? (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-green-50 border border-green-200 rounded-xl p-5 text-center">
              <div className="text-3xl font-bold text-green-700">{waStats.total || 0}</div>
              <div className="text-sm text-green-600 mt-1">WhatsApp</div>
              <div className="text-xs text-green-500">{waStats.respondidas || 0} respondidas</div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 text-center">
              <div className="text-3xl font-bold text-blue-700">{emailStats.total || 0}</div>
              <div className="text-sm text-blue-600 mt-1">Email</div>
              <div className="text-xs text-blue-500">{emailStats.respondidas || 0} respondidas</div>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-xl p-5 text-center">
              <div className="text-3xl font-bold text-purple-700">{totalConv}</div>
              <div className="text-sm text-purple-600 mt-1">Total conversaciones</div>
              <div className="text-xs text-purple-500">{totalResp} respondidas</div>
            </div>
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-5 text-center">
              <div className="text-3xl font-bold text-orange-700">{waStats.avg_ms || 0}ms</div>
              <div className="text-sm text-orange-600 mt-1">Tiempo medio</div>
              <div className="text-xs text-orange-500">respuesta</div>
            </div>
          </div>

          {data.top_topics && data.top_topics.length > 0 && (
            <div className="bg-white rounded-xl shadow overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b">
                <h3 className="text-sm font-semibold text-gray-700">Topics mas consultados (WhatsApp)</h3>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs text-gray-500">Topic</th>
                    <th className="px-4 py-2 text-right text-xs text-gray-500">Veces</th>
                    <th className="px-4 py-2 text-right text-xs text-gray-500">Confianza media</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.top_topics.map((t, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-4 py-2 font-medium text-gray-800">{t.chosen_topic}</td>
                      <td className="px-4 py-2 text-right text-gray-600">{t.veces}</td>
                      <td className="px-4 py-2 text-right text-gray-600">{t.avg_sim}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}

function AlcazarDashboard() {
  const { user, logout } = useAuth();
  const [emailData, setEmailData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const SHOW_FINANZAS = false;
  const userRole = user?.role || user?.global_role;
  const isAdmin = ['superadmin', 'admin'].includes(userRole);
  const [tab, setTab] = useState('eventos');
  const [expandedRow, setExpandedRow] = useState(null);
  const [translations, setTranslations] = useState({});
  const [translating, setTranslating] = useState({});

  // Server-side filters
  const [days, setDays] = useState('7');
  const [fChannel, setFChannel] = useState('all');
  const [fStatus, setFStatus] = useState('all');
  const [fMode, setFMode] = useState('real');
  const [searchInput, setSearchInput] = useState('');
  const [activeSearch, setActiveSearch] = useState('');
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);
  const [replyMsg, setReplyMsg] = useState(null);
  const [quickFilter, setQuickFilter] = useState('all');

  const fetchData = useCallback(async () => {
    try {
      const params = { days, limit: '200' };
      if (fChannel !== 'all') params.channel = fChannel;
      if (fStatus !== 'all') params.status = fStatus;
      if (activeSearch) params.search = activeSearch;

      const emailRes = await api.get('/api/alcazar/dashboard/email', { params });
      if (emailRes.data.success) setEmailData(emailRes.data);
      setLastUpdate(new Date());
      setError(null);
    } catch (err) {
      if (err.response?.status === 401) return;
      setError(err.message || 'Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [days, fChannel, fStatus, activeSearch]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (!autoRefresh || tab !== 'eventos') return;
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, tab, fetchData]);

  const handleSearchKey = (e) => { if (e.key === 'Enter') setActiveSearch(searchInput); };

  const resetFilters = () => {
    setDays('7'); setFChannel('all'); setFStatus('all');
    setSearchInput(''); setActiveSearch(''); setQuickFilter('all');
    setFMode('real');
  };


  const handleReply = async (logEntry) => {
    if (!replyText.trim() || replyText.trim().length < 5) {
      setReplyMsg({ type: 'error', text: 'La respuesta debe tener al menos 5 caracteres' });
      return;
    }
    setReplying(true);
    setReplyMsg(null);
    try {
      const pendingRes = await api.get('/api/alcazar/pendientes');
      const pendingList = Array.isArray(pendingRes.data) ? pendingRes.data : (pendingRes.data?.handoffs || []);
      const pending = pendingList.find(p => 
        p.contact_identifier === logEntry.from_email && 
        (p.subject || '').includes((logEntry.subject || '').substring(0, 30))
      );
      if (!pending) {
        setReplyMsg({ type: 'error', text: 'No se encontro el registro pendiente' });
        setReplying(false);
        return;
      }
      const res = await api.post('/api/alcazar/pendientes/' + pending.id + '/enviar-respuesta', {
        response_text: replyText.trim()
      });
      if (res.data?.success) {
        setReplyMsg({ type: 'success', text: 'Respuesta enviada correctamente' });
        setReplyText('');
        fetchData();
      } else {
        setReplyMsg({ type: 'error', text: res.data?.error || 'Error al enviar' });
      }
    } catch (err) {
      setReplyMsg({ type: 'error', text: err.response?.data?.error || err.message });
    }
    setReplying(false);
  };

  const handleTranslate = async (logId, field, text) => {
    const key = logId + '_' + field;
    if (translations[key]) return;
    setTranslating(prev => ({ ...prev, [key]: true }));
    try {
      const res = await api.post('/api/alcazar/translate', { text, targetLang: 'es' });
      if (res.data.success) {
        setTranslations(prev => ({ ...prev, [key]: res.data.translated }));
      }
    } catch (e) {
      console.error('Translation error:', e);
    } finally {
      setTranslating(prev => ({ ...prev, [key]: false }));
    }
  };

  const rawLogs = emailData?.logs || [];
  const stats = emailData?.stats;

  // Filter: test/real (uses backend is_test flag) + quick filters
  const logs = React.useMemo(() => {
    let filtered = rawLogs;
    // Mode filter (test vs real) — is_test comes from backend
    if (fMode === 'real') filtered = filtered.filter(l => !l.is_test);
    else if (fMode === 'test') filtered = filtered.filter(l => l.is_test);
    // Quick filters
    if (quickFilter === 'all') return filtered;
    if (quickFilter === 'stuck') return filtered.filter(l => l.global_status === 'WAITING_ENTRADAS' || l.global_status === 'MANUAL_REQUIRED');
    if (quickFilter === 'failed') return filtered.filter(l => l.global_status === 'FAILED');
    if (quickFilter === 'handoff') return filtered.filter(l => l.event_type === 'handoff');
    return filtered;
  }, [rawLogs, quickFilter, fMode]);
  const kpiCount = 6;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header style={{ background: "linear-gradient(to right, #1890d7, #1a9fe0)" }} className="text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="relative flex items-center min-h-[56px]">
            {/* Logo izquierda */}
            <div className="flex items-center gap-3">
              <img src="/logo-infoticketing.png" alt="Infoticketing" className="h-16 brightness-0 invert"
                onError={(e) => { e.target.style.display = 'none'; }} />
            </div>

            {/* Titulo centrado absoluto con logo Alcázar */}
            <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-3">
              <img src="/logo-alcazar.webp" alt="Real Alcázar de Sevilla" className="h-12 object-contain drop-shadow-md"
                onError={(e) => { e.target.style.display = 'none'; }} />
              <div className="text-center leading-tight">
                <div className="text-xl font-semibold text-white">Real Alcázar de Sevilla</div>
                <div className="text-sm text-blue-100">XIA Dashboard</div>
              </div>
            </div>

            {/* Controles + usuario derecha */}
            <div className="ml-auto flex items-center gap-4">
              {tab === 'eventos' && (
                <>
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={autoRefresh}
                      onChange={(e) => setAutoRefresh(e.target.checked)} className="rounded" />
                    Auto-refresh
                  </label>
                  <button onClick={fetchData}
                    className="px-3 py-1.5 bg-white/20 hover:bg-white/30 text-white rounded-lg transition text-sm">
                    Actualizar
                  </button>
                  {lastUpdate && (
                    <span className="text-xs text-blue-100">{lastUpdate.toLocaleTimeString()}</span>
                  )}
                </>
              )}
              <div className="flex items-center gap-2 text-sm border-l border-white/30 pl-4">
                <span className="text-white">{user?.name || user?.email}</span>
                <button onClick={logout}
                  className="px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded text-white transition text-xs">
                  Salir
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {error && (
          <div className="mb-6 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">{error}</div>
        )}



        <div className="flex gap-2 mb-4 flex-wrap">
          <button onClick={() => setTab('eventos')}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              tab === 'eventos' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            Eventos ({logs.length})
          </button>
          {isAdmin && <button onClick={() => setTab('conocimiento')}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              tab === 'conocimiento' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            Conocimiento
          </button>}
          {isAdmin && <button onClick={() => setTab('reenviadas')}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              tab === 'reenviadas' ? 'bg-amber-600 text-white' : 'bg-amber-50 text-amber-700 hover:bg-amber-100'}`}>
            Reenviadas
          </button>}
          {isAdmin && <button onClick={() => setTab('agente')}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              tab === 'agente' ? 'bg-green-600 text-white' : 'bg-green-50 text-green-700 hover:bg-green-100'}`}>
            Agente Operativo
          </button>}
          <button onClick={() => setTab('reporting_v4')}
            className={`px-4 py-2 rounded-lg font-medium transition ${
              tab === 'reporting_v4' ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            Reporting
          </button>
        </div>

        {tab === 'eventos' && (
          <>
            {/* Filter bar */}
            <div className="bg-white rounded-lg shadow p-4 mb-4">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-sm font-semibold text-gray-700">Filtros:</span>
                <select value={days} onChange={e => setDays(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white">
                  <option value="1">Hoy</option>
                  <option value="3">3 dias</option>
                  <option value="7">7 dias</option>
                  <option value="15">15 dias</option>
                  <option value="30">30 dias</option>
                </select>
                <select value={fChannel} onChange={e => setFChannel(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white">
                  <option value="all">Procedencia: Todos</option>
                  <option value="email">Email</option>
                  <option value="whatsapp">WhatsApp</option>
                  <option value="chat">Chat Web</option>
                  <option value="llamadas">Llamadas</option>
                  <option value="mia">MIA (Tests)</option>
                </select>
                <select value={fStatus} onChange={e => setFStatus(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white">
                  <option value="all">Estado: Todos</option>
                  <option value="draft_created">Borrador OK</option>
                  <option value="draft_error">Error Draft</option>
                  <option value="learned">Aprendido</option>
                  <option value="handoff">Handoff</option>
                  <option value="ignored">Ignorado</option>
                </select>
                <select value={fMode} onChange={e => setFMode(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white"
                  style={{fontWeight: fMode !== 'all' ? 600 : 400, color: fMode === 'test' ? '#9333ea' : fMode === 'real' ? '#059669' : '#374151'}}>
                  <option value="real">Solo reales</option>
                  <option value="all">Todos</option>
                  <option value="test">Solo test</option>
                </select>
                <div className="flex items-center gap-1 flex-1 min-w-[200px]">
                  <input type="text" placeholder="Buscar por asunto, email, contenido..."
                    value={searchInput} onChange={e => setSearchInput(e.target.value)}
                    onKeyDown={handleSearchKey}
                    className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm flex-1" />
                  <button onClick={() => setActiveSearch(searchInput)}
                    className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">
                    Buscar
                  </button>
                </div>
                <button onClick={resetFilters}
                  className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-600">
                  Limpiar
                </button>
              </div>
            </div>

            {/* Quick filter buttons */}
            <div className="flex gap-2 mb-3">
              {[
                { key: 'all', label: 'Todos', count: rawLogs.length },
                { key: 'stuck', label: 'Atascados', count: rawLogs.filter(l => l.global_status === 'WAITING_ENTRADAS' || l.global_status === 'MANUAL_REQUIRED').length, color: '#f59e0b' },
                { key: 'failed', label: 'Fallos', count: rawLogs.filter(l => l.global_status === 'FAILED').length, color: '#ef4444' },
                { key: 'handoff', label: 'Handoff', count: rawLogs.filter(l => l.event_type === 'handoff').length, color: '#eab308' },
              ].map(f => (
                <button key={f.key} onClick={() => setQuickFilter(f.key)}
                  style={{
                    padding: '6px 14px', borderRadius: '8px', border: 'none',
                    fontWeight: quickFilter === f.key ? 700 : 500, fontSize: '13px',
                    backgroundColor: quickFilter === f.key ? (f.color || '#1f2937') : '#f3f4f6',
                    color: quickFilter === f.key ? '#fff' : '#374151',
                    cursor: 'pointer', transition: 'all 0.15s',
                  }}
                >
                  {f.label} {f.count > 0 && <span style={{ marginLeft: '4px', opacity: 0.8 }}>({f.count})</span>}
                </button>
              ))}
            </div>

            {/* Events table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full" >
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase w-10"></th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap">Fecha</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Procedencia</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">De</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Asunto</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Intent</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Accion</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tiempo</th>
                      <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">Adj.</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {logs.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="px-4 py-8 text-center text-gray-500">
                          No hay eventos que mostrar
                        </td>
                      </tr>
                    ) : (
                      logs.map(log => (
                        <React.Fragment key={log.id}>
                          <tr className={`hover:bg-gray-50 cursor-pointer ${expandedRow === log.id ? 'bg-blue-50' : ''}`}
                            onClick={() => setExpandedRow(expandedRow === log.id ? null : log.id)}>
                            <td className="px-2 py-3 text-center">
                              <span className="text-gray-400 text-lg select-none">
                                {expandedRow === log.id ? '\u25BC' : '\u25B6'}
                              </span>
                            </td>
                            <td className="px-3 py-3 text-sm text-gray-600 whitespace-nowrap">
                              {new Date(log.timestamp).toLocaleString('es-ES', {
                                day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
                              })}
                            </td>
                            <td className="px-3 py-3">
                              <ChannelBadge channel={log.channel || 'email'} />
                            </td>
                            <td className="px-3 py-3">
                              <EventBadge type={log.event_type} />
                    {(log.event_type === 'handoff' || log.event_type === 'draft_created' || log.event_type === 'email_sent') && (
                      <div style={{ marginTop: '4px' }}>
                        <LifecycleIcons lifecycle={log.lifecycle} eventType={log.event_type} />
                      </div>
                    )}
                            </td>
                            <td className="px-3 py-3 text-sm text-gray-800" style={{maxWidth: '220px', wordBreak: 'break-all'}}>
                              {log.from_email ? (
                                <span style={{display: 'flex', alignItems: 'center', gap: '4px'}}>
                                  <span title={log.from_email} style={{wordBreak: 'break-all'}}>{log.from_email}</span>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigator.clipboard.writeText(log.from_email);
                                      const btn = e.currentTarget;
                                      btn.textContent = '\u2713';
                                      btn.style.color = '#22c55e';
                                      setTimeout(() => { btn.textContent = '\u29C9'; btn.style.color = ''; }, 1200);
                                    }}
                                    title="Copiar email"
                                    className="text-gray-400 hover:text-blue-500 flex-shrink-0"
                                    style={{background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', padding: '0 2px', lineHeight: 1}}
                                  >{'\u29C9'}</button>
                                </span>
                              ) : <span className="text-gray-400">-</span>}
                            </td>
                            <td className="px-3 py-3 text-sm text-gray-800" style={{maxWidth: '250px', wordBreak: 'break-word'}} title={log.subject}>
                              {log.subject || <span className="text-gray-400">-</span>}
                            </td>
                            <td className="px-3 py-3">
                              <IntentBadge intent={log.detected_intent} confidence={log.intent_confidence} kbTopic={log.kb_topic || (() => { try { return JSON.parse(log.metadata || '{}').kb_topic } catch { return null } })()} kbId={(() => { try { return JSON.parse(log.metadata || '{}').kb_id } catch { return null } })()} />
                            </td>
                            <td className="px-3 py-3">
                              <ActionBadge action={log.xia_action} />
                            </td>
                            <td className="px-3 py-3">
                              <GlobalStatusBadge status={log.global_status} />
                            </td>
                            <td className="px-3 py-3 text-sm text-gray-600" title={log.sla_display ? 'SLA: ' + log.sla_display : ''}>
                              {log.sla_display || (log.response_time_ms ? (log.response_time_ms >= 1000 ? (log.response_time_ms/1000).toFixed(1) + 's' : log.response_time_ms + 'ms') : '-')}
                            </td>
                            <td className="px-3 py-3 text-center">
                              {(() => { try { const m = JSON.parse(log.metadata || '{}'); const atts = m.attachments || []; return atts.length > 0 ? <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-xs font-medium"><svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"/></svg>{atts.length}</span> : <span className="text-gray-300">-</span>; } catch { return <span className="text-gray-300">-</span>; } })()}
                            </td>
                          </tr>
                          {expandedRow === log.id && (
                            <tr>
                              <td colSpan={10} className="px-0 py-0">
                                <div className="bg-gray-50 border-t border-b border-blue-200 px-6 py-4">
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                      <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Mensaje del cliente</h4>
                                      <div className="bg-white rounded-lg border p-3 text-sm text-gray-700 whitespace-pre-wrap max-h-64 overflow-y-auto">
                                        {decodeHtml(log.email_body) || <span className="text-gray-400 italic">Sin contenido</span>}
                                      </div>
                                      {log.email_body && !translations[log.id + '_body'] && (
                                        <button onClick={(e) => { e.stopPropagation(); handleTranslate(log.id, 'body', log.email_body); }}
                                          className="mt-2 text-xs text-blue-600 hover:text-blue-800 underline">
                                          {translating[log.id + '_body'] ? 'Traduciendo...' : 'Traducir al español'}
                                        </button>
                                      )}
                                      {translations[log.id + '_body'] && (
                                        <div className="mt-2 bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-gray-700 whitespace-pre-wrap">
                                          <span className="text-xs font-semibold text-yellow-700 uppercase block mb-1">Traduccion:</span>
                                          {translations[log.id + '_body']}
                                        </div>
                                      )}
                                    </div>
                                    <div>
                                      <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Respuesta XIA</h4>
                                      <div className="bg-white rounded-lg border p-3 text-sm text-gray-700 whitespace-pre-wrap max-h-64 overflow-y-auto">
                                        {decodeHtml(log.response_text) || <span className="text-gray-400 italic">Sin respuesta (handoff)</span>}
                                      </div>
                                      {log.response_text && !translations[log.id + '_response'] && (
                                        <button onClick={(e) => { e.stopPropagation(); handleTranslate(log.id, 'response', log.response_text); }}
                                          className="mt-2 text-xs text-blue-600 hover:text-blue-800 underline">
                                          {translating[log.id + '_response'] ? 'Traduciendo...' : 'Traducir al español'}
                                        </button>
                                      )}
                                      {translations[log.id + '_response'] && (
                                        <div className="mt-2 bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-gray-700 whitespace-pre-wrap">
                                          <span className="text-xs font-semibold text-yellow-700 uppercase block mb-1">Traduccion:</span>
                                          {translations[log.id + '_response']}
                                        </div>
                                      )}
                                    {log.lifecycle && (
                                      <div style={{ marginTop: '12px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                        <div style={{ padding: '12px', backgroundColor: '#ecfdf5', borderRadius: '8px', border: '1px solid #a7f3d0' }}>
                                          <div style={{ fontWeight: 600, marginBottom: '6px', color: '#065f46', fontSize: '13px' }}>
                                            Respuesta de Entradas
                                            {log.lifecycle.answered_at && (
                                              <span style={{ fontWeight: 400, marginLeft: '8px', fontSize: '11px', color: '#6b7280' }}>
                                                {new Date(log.lifecycle.answered_at).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                                              </span>
                                            )}
                                          </div>
                                          {log.lifecycle.entradas_response ? (
                                            <div style={{ whiteSpace: 'pre-wrap', fontSize: '13px', color: '#374151' }}>
                                              {log.lifecycle.entradas_response}
                                            </div>
                                          ) : (
                                            <div style={{ color: '#9ca3af', fontStyle: 'italic', fontSize: '13px' }}>
                                              Pendiente de respuesta...
                                            </div>
                                          )}
                                        </div>
                                        <div style={{ padding: '12px', backgroundColor: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
                                          <div style={{ fontWeight: 600, marginBottom: '6px', color: '#1e40af', fontSize: '13px' }}>
                                            XIA envio al cliente
                                          </div>
                                          {log.lifecycle.xia_sent_response ? (
                                            <div style={{ whiteSpace: 'pre-wrap', fontSize: '13px', color: '#374151' }}>
                                              {log.lifecycle.xia_sent_response}
                                            </div>
                                          ) : (
                                            <div style={{ color: '#9ca3af', fontStyle: 'italic', fontSize: '13px' }}>
                                              {log.lifecycle.entradas_replied ? 'Procesando...' : 'Esperando respuesta de entradas...'}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                    </div>
                                  </div>
                                  {/* XIA Analysis Panel */}
                                  {(log.detected_intent || log.xia_action || log.extraction) && (
                                    <XiaDetailPanel log={log} />
                                  )}
            {log.event_type === 'handoff' && (!log.lifecycle || !log.lifecycle.sent_to_customer) && (
              <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#fef3c7', borderRadius: '8px', border: '1px solid #fbbf24' }}>
                <div style={{ fontWeight: 600, marginBottom: '8px', color: '#92400e', fontSize: '13px' }}>
                  Responder manualmente al cliente
                </div>
                <textarea
                  value={replyText}
                  onChange={e => setReplyText(e.target.value)}
                  placeholder="Escribe la respuesta para el cliente..."
                  style={{ width: '100%', minHeight: '80px', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px', fontFamily: 'inherit', resize: 'vertical' }}
                />
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px' }}>
                  <button
                    onClick={() => handleReply(log)}
                    disabled={replying || !replyText.trim()}
                    style={{
                      padding: '8px 20px', borderRadius: '6px', border: 'none',
                      backgroundColor: replying ? '#9ca3af' : '#059669', color: '#fff',
                      fontWeight: 600, cursor: replying ? 'wait' : 'pointer', fontSize: '13px'
                    }}
                  >
                    {replying ? 'Enviando...' : 'Enviar respuesta'}
                  </button>
                  {replyMsg && (
                    <span style={{ fontSize: '13px', color: replyMsg.type === 'success' ? '#059669' : '#dc2626' }}>
                      {replyMsg.text}
                    </span>
                  )}
                </div>
              </div>
            )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <div className="bg-gray-50 px-4 py-2 text-sm text-gray-500 border-t">
                Mostrando {logs.length} registros
              </div>
            </div>
          </>
        )}

        {tab === 'conocimiento' && isAdmin && <AlcazarKnowledge />}
        {tab === 'reenviadas' && isAdmin && <AlcazarReenviadas />}
        {tab === 'agente' && isAdmin && <AlcazarAgente />}

        {tab === 'procedimientos' && isAdmin && (
          <div className="space-y-6">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <h2 className="text-amber-800 font-semibold text-lg mb-1">Procedimientos de Gobernanza XIA</h2>
              <p className="text-amber-700 text-sm">Reglas fijas del sistema. Para modificarlas, contactar con el equipo tecnico.</p>
            </div>

            {/* FLUJO VISUAL */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-semibold text-gray-800 text-lg mb-4 border-b pb-2">Flujo de procesamiento</h3>
              <div className="flex flex-col items-center gap-0">

                {/* Step 1 */}
                <div className="w-full max-w-md bg-sky-100 border border-sky-300 rounded-lg px-4 py-3 text-center">
                  <div className="font-semibold text-sky-900 text-sm">1. Entra consulta del usuario</div>
                </div>
                <div className="text-gray-400 text-lg leading-none py-0.5">&#9660;</div>

                {/* Step 2 */}
                <div className="w-full max-w-md bg-gray-100 border border-gray-300 rounded-lg px-4 py-3 text-center">
                  <div className="font-semibold text-gray-800 text-sm">2. Normalizer</div>
                  <div className="text-xs text-gray-500 mt-1">Limpia firmas, aviso legal, ruido</div>
                </div>
                <div className="text-gray-400 text-lg leading-none py-0.5">&#9660;</div>

                {/* Step 3 */}
                <div className="w-full max-w-md bg-indigo-100 border border-indigo-300 rounded-lg px-4 py-3 text-center">
                  <div className="font-semibold text-indigo-900 text-sm">3. Detectar idioma + intent</div>
                </div>
                <div className="text-gray-400 text-lg leading-none py-0.5">&#9660;</div>

                {/* Step 4 - Three branches */}
                <div className="w-full grid grid-cols-3 gap-3">
                  {/* 4A Patronato */}
                  <div className="flex flex-col items-center gap-0">
                    <div className="bg-orange-100 border border-orange-300 rounded-lg px-3 py-3 text-center w-full">
                      <div className="font-semibold text-orange-900 text-xs">4A. Whitelist Patronato</div>
                      <div className="text-xs text-orange-700 mt-1">reclamacion_formal<br/>reservas_escolares</div>
                    </div>
                    <div className="text-gray-400 text-lg leading-none py-0.5">&#9660;</div>
                    <div className="bg-orange-50 border border-orange-200 rounded-lg px-3 py-2 text-center w-full">
                      <div className="font-semibold text-orange-800 text-xs">HANDOFF PATRONATO</div>
                    </div>
                    <div className="text-gray-400 text-lg leading-none py-0.5">&#9660;</div>
                    <div className="bg-orange-50 border border-orange-200 rounded-lg px-3 py-2 text-center w-full">
                      <div className="text-xs text-orange-700">Respuesta Patronato</div>
                    </div>
                  </div>

                  {/* 4B XIA sabe */}
                  <div className="flex flex-col items-center gap-0">
                    <div className="bg-green-100 border border-green-300 rounded-lg px-3 py-3 text-center w-full">
                      <div className="font-semibold text-green-900 text-xs">4B. XIA sabe</div>
                      <div className="text-xs text-green-700 mt-1">Knowledge valida</div>
                    </div>
                    <div className="text-gray-400 text-lg leading-none py-0.5">&#9660;</div>
                    <div className="bg-green-50 border border-green-200 rounded-lg px-3 py-2 text-center w-full">
                      <div className="font-semibold text-green-800 text-xs">Genera respuesta</div>
                      <div className="text-xs text-green-600 mt-0.5">Aplica reglas + gobernanza</div>
                    </div>
                    <div className="text-gray-400 text-lg leading-none py-0.5">&#9660;</div>
                    <div className="bg-purple-50 border border-purple-200 rounded-lg px-3 py-2 text-center w-full">
                      <div className="font-semibold text-purple-800 text-xs">Traduccion</div>
                      <div className="text-xs text-purple-600 mt-0.5">Literal de sentido</div>
                    </div>
                  </div>

                  {/* 4C XIA no sabe */}
                  <div className="flex flex-col items-center gap-0">
                    <div className="bg-yellow-100 border border-yellow-300 rounded-lg px-3 py-3 text-center w-full">
                      <div className="font-semibold text-yellow-900 text-xs">4C. XIA no sabe</div>
                      <div className="text-xs text-yellow-700 mt-1">Unknown / sin KB</div>
                    </div>
                    <div className="text-gray-400 text-lg leading-none py-0.5">&#9660;</div>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2 text-center w-full">
                      <div className="font-semibold text-yellow-800 text-xs">HANDOFF ENTRADAS</div>
                    </div>
                    <div className="text-gray-400 text-lg leading-none py-0.5">&#9660;</div>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2 text-center w-full">
                      <div className="text-xs text-yellow-700">Espera resp. humana</div>
                    </div>
                    <div className="text-gray-400 text-lg leading-none py-0.5">&#9660;</div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 text-center w-full">
                      <div className="font-semibold text-blue-800 text-xs">Memoriza</div>
                      <div className="text-xs text-blue-600 mt-0.5">Limpia + guarda UUID</div>
                    </div>
                  </div>
                </div>

                {/* Convergence */}
                <div className="text-gray-400 text-lg leading-none py-0.5">&#9660;</div>
                <div className="w-full max-w-md bg-sky-100 border border-sky-300 rounded-lg px-4 py-3 text-center">
                  <div className="font-semibold text-sky-900 text-sm">7. Responde al usuario</div>
                  <div className="text-xs text-sky-700 mt-1">Mismo idioma &middot; URLs oficiales</div>
                </div>
                <div className="text-gray-400 text-lg leading-none py-0.5">&#9660;</div>
                <div className="w-full max-w-md bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-center">
                  <div className="font-semibold text-gray-700 text-sm">8. Almacena para futuro</div>
                  <div className="text-xs text-gray-500 mt-1">Queda en KB para proximas consultas similares</div>
                </div>
              </div>

              {/* Legend */}
              <div className="mt-6 pt-4 border-t border-gray-200">
                <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Resumen de gobernanza</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs text-gray-600">
                  <div className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-orange-200 border border-orange-300 inline-block"></span> Whitelist Patronato &#8594; solo reclamacion_formal + reservas_escolares</div>
                  <div className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-green-200 border border-green-300 inline-block"></span> XIA sabe &#8594; responde + traduce</div>
                  <div className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-yellow-200 border border-yellow-300 inline-block"></span> No sabe &#8594; HANDOFF_ENTRADAS + memoriza</div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6 space-y-6">
              <div>
                <h3 className="font-semibold text-gray-800 text-lg mb-3 border-b pb-2">1. Gobernanza de comunicacion</h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2"><span className="text-green-600 font-bold mt-0.5">&#10003;</span><span>El cliente recibe respuesta desde <strong>alcazardesevilla@manantialdeideas.com</strong></span></li>
                  <li className="flex items-start gap-2"><span className="text-green-600 font-bold mt-0.5">&#10003;</span><span>Si XIA no sabe la respuesta &#8594; <strong>HANDOFF_ENTRADAS a entradas@manantialdeideas.com</strong></span></li>
                  <li className="flex items-start gap-2"><span className="text-red-600 font-bold mt-0.5">&#10007;</span><span><strong>Patronato NUNCA es fallback.</strong> Solo se utiliza para la whitelist definida en el punto 2</span></li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800 text-lg mb-3 border-b pb-2">2. Whitelist de Patronato</h3>
                <p className="text-sm text-gray-600 mb-3">UNICAMENTE estos dos intents se derivan al Patronato del Alcazar:</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <div className="font-semibold text-orange-800">reclamacion_formal</div>
                    <div className="text-sm text-orange-700 mt-1">Reclamaciones oficiales, hojas de reclamacion</div>
                  </div>
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                    <div className="font-semibold text-orange-800">reservas_escolares</div>
                    <div className="text-sm text-orange-700 mt-1">Reservas de grupos escolares, visitas educativas</div>
                  </div>
                </div>

                <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="text-xs font-semibold text-blue-800 uppercase mb-2">Texto obligatorio para reservas_escolares (handoff Patronato):</div>
                  <div className="text-sm text-blue-900 leading-relaxed">
                    <p>Si los visitantes son alumnos desde infantil hasta 2&#186; de la ESO, pueden hacer la reserva a traves de <a href="https://alcazarsevilla.org/centroseducativos/" target="_blank" rel="noopener noreferrer" className="underline font-medium">https://alcazarsevilla.org/centroseducativos/</a></p>
                    <p className="mt-2">Para el resto de grupos, no contamos con un procedimiento especial, pueden comprobar la disponibilidad y las tarifas, asi como hacer la reserva a traves de la web <a href="https://alcazarsevilla.org/prepara-la-visita/" target="_blank" rel="noopener noreferrer" className="underline font-medium">https://alcazarsevilla.org/prepara-la-visita/</a></p>
                  </div>
                </div>

                <p className="text-xs text-gray-500 mt-3">Todo lo demas &#8594; HANDOFF_ENTRADAS (nunca Patronato)</p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800 text-lg mb-3 border-b pb-2">3. Reglas de intents prioritarios</h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2"><span className="text-blue-600 font-bold mt-0.5">&#9679;</span><span><strong>cambios / reembolso</strong> &#8594; SIEMPRE genera borrador (SEND_DRAFT), NUNCA Patronato</span></li>
                  <li className="flex items-start gap-2"><span className="text-blue-600 font-bold mt-0.5">&#9679;</span><span><strong>solicitud_factura</strong> &#8594; SIEMPRE responde con URL <a href="https://facturacion.tenemosplan.com" target="_blank" rel="noopener noreferrer" className="underline">facturacion.tenemosplan.com</a>, NUNCA handoff</span></li>
                  <li className="flex items-start gap-2"><span className="text-blue-600 font-bold mt-0.5">&#9679;</span><span><strong>correccion_documento_id</strong> &#8594; Prioridad maxima (nombre/apellido/DNI incorrecto en entrada)</span></li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800 text-lg mb-3 border-b pb-2">4. Idiomas no latinos</h3>
                <p className="text-sm text-gray-700">Si el idioma detectado es <strong>zh, ko, ar, ru, ja, nl</strong> y el intent es <strong>unknown</strong> &#8594; HANDOFF_ENTRADAS (no Patronato)</p>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800 text-lg mb-3 border-b pb-2">5. URLs oficiales</h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2"><span className="text-green-600 font-bold mt-0.5">&#10003;</span><span>Compra de entradas: <strong><a href="https://alcazardesevilla.infoticketing.com/" target="_blank" rel="noopener noreferrer" className="underline">https://alcazardesevilla.infoticketing.com/</a></strong></span></li>
                  <li className="flex items-start gap-2"><span className="text-green-600 font-bold mt-0.5">&#10003;</span><span>Solicitud de factura: <strong><a href="https://facturacion.tenemosplan.com" target="_blank" rel="noopener noreferrer" className="underline">https://facturacion.tenemosplan.com</a></strong></span></li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800 text-lg mb-3 border-b pb-2">6. Canales de atencion</h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2"><span className="text-green-600 font-bold mt-0.5">&#10003;</span><span><strong>Email</strong> &#8594; Canal principal y unico canal facturable. Gestionado por XIA + handoff a Entradas / Patronato</span></li>
                </ul>
                <div className="mt-3 bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs text-gray-500">
                  <strong>Definicion facturable:</strong> Cada email procesado por el pipeline (registrado en xia_email_worker_logs con channel=email). No se contabilizan tests, webchat ni otros canales.
                </div>
              </div>
            </div>
          </div>
        )}

        {tab === 'reporting' && <AlcazarReporting />}

        {tab === 'costes' && isAdmin && (
          <CostesTab />
        )}

        {tab === 'reporting_v4' && <ReportingV4 />}

        <div className="mt-6 text-center text-xs text-gray-400">
          XIA v4 RAG &middot; Real Alcazar de Sevilla &middot; Dashboard
        </div>
      </main>
    
      {/* Floating test chat widget */}
      <AlcazarTestChat />
    </div>
  );
}

export default AlcazarDashboard;
